import { ReactNode, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { BookingSiteProvider, basemap as basemapFor } from "@1-now/booking-core";
import type { BookingSite, BookingSiteApi } from "@1-now/booking-core";
import { useData } from "data/data-provider";
import serverURL from "api/auth/auth";
import {
  Booking,
  cancelBooking,
  deleteTripPhoto,
  getBonzahAlerts,
  getBooking,
  getPreviousBooking,
  getTripPhotos,
  retriveBooking,
  tripModificationRequest,
  uploadTripPhotos,
} from "api/booking/booking";
import { AddCustomer, getCustomerFiles, uploadCustomerFiles } from "api/customer/customer";
import { CreateCanopyVerification } from "api/Canopy/canopy";
import {
  CreateStripeVerification,
  CreateVouchVerification,
  PaymentDone,
  SavePaymentMethod,
} from "api/stripe/stripe";
import { getPrivacyPolicyContent } from "api/rental-agreement/rental-agreement";
import { chargeSquareCard, SaveSquareCard } from "api/square/square";
import { readOdometer } from "api/vehicles/vehicle";
import { getInvoice } from "api/Invoice/invoice";
import { getAgreementPdfBase } from "utils/agreement-url";
import { getAccessToken, getStoredRenterUser } from "utils/renterSession";

/* Stage's copy of QA's bridge (src/lib/bookingSite.tsx). Stage has no Design C,
   so its long-term series call and map tiles are left out; nothing on stage
   asks for them. Without this provider every @1-now/booking-core hook that
   reads the site (useSquarePayment on the car page and checkout) threw and the
   page fell into its error boundary. */
const READ_TIMEOUT_MS = 30_000;

const api: BookingSiteApi = {
  Booking,
  getBooking,
  cancelBooking,
  retriveBooking,
  tripModificationRequest,
  getTripPhotos,
  uploadTripPhotos,
  deleteTripPhoto,
  readOdometer,
  AddCustomer,
  getCustomerFiles,
  uploadCustomerFiles,
  CreateCanopyVerification,
  CreateStripeVerification,
  CreateVouchVerification,
  getPrivacyPolicyContent,
  fetchSeries: () => Promise.reject(new Error("Long-term series are a Design C feature, not on this build.")),
  SavePaymentMethod,
  PaymentDone,
  SaveSquareCard,
  chargeSquareCard,
  getOtherBookings: (bookingUuid: string, offset = 0) => getPreviousBooking(bookingUuid, offset),
  getInvoice,
  getBonzahAlerts,
};

const session = { getAccessToken, getStoredRenterUser };

const basemap = () =>
  basemapFor({
    token: process.env.REACT_APP_MAPBOX_TOKEN,
    style: process.env.REACT_APP_MAPBOX_STYLE,
  });

const agreementViewerUrl = (bookingUuid: string) =>
  `${getAgreementPdfBase()}${encodeURIComponent(bookingUuid)}`;

/** Gives the shared booking screens and hooks in @1-now/booking-core this site's
 *  store, API client, router and build settings. Mounted once, inside the data
 *  provider and the router (index.tsx). */
export const BookingSiteBridge = ({ children }: { children?: ReactNode }) => {
  const data = useData();
  const navigate = useNavigate();
  const location = useLocation();
  const site = useMemo<BookingSite>(
    () => ({
      ourCompany: data.ourCompany,
      companyDetails: data.companyDetails,
      ourLocation: data.ourLocation,
      startDate: data.startDate,
      endDate: data.endDate,
      startTime: data.startTime,
      endTime: data.endTime,
      pickupLocation: data.pickupLocation,
      dropOffLocation: data.dropOffLocation,
      setStartDate: data.setStartDate,
      setEndDate: data.setEndDate,
      setStartTime: data.setStartTime,
      setEndTime: data.setEndTime,
      setPickupLocation: data.setPickupLocation,
      setDropoffLocation: data.setDropoffLocation,
      setSearchedPickupLocation: data.setSearchedPickupLocation,
      setSearchedDropoffLocation: data.setSearchedDropoffLocation,
      selectedCar: data.selectedCar,
      bookingdetails: data.bookingdetails,
      setBookingdetails: data.setBookingdetails,
      callCouponAPI: data.callCouponAPI,
      callremoveCouponAPI: data.callremoveCouponAPI,
      api,
      session,
      navigate,
      location,
      serverURL,
      readTimeoutMs: READ_TIMEOUT_MS,
      basemap,
      config: {
        vouchedPublicKey: process.env.REACT_APP_VOUCHED_PUBLIC_KEY,
        canopyPublicAlias: process.env.REACT_APP_CANOPY_PUBLIC_ALIAS,
        routingUrl: process.env.REACT_APP_ROUTING_URL,
        agreementViewerUrl,
      },
    }),
    [data, navigate, location],
  );
  return <BookingSiteProvider value={site}>{children}</BookingSiteProvider>;
};

export default BookingSiteBridge;
